import { alpha, styled } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ModelTrainingRoundedIcon from '@mui/icons-material/ModelTrainingRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';

const MainCard = styled(Card)(({ theme }) => ({
  borderRadius: 18,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(3),
}));

const Banner = styled(Box)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.32)}`,
  backgroundColor: alpha(theme.palette.primary.main, 0.06),
  padding: theme.spacing(1.4, 1.6),
}));

const DataHeader = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '48px 1.6fr 0.9fr 0.6fr 1fr 1.2fr 0.8fr 52px',
  gap: theme.spacing(1.5),
  alignItems: 'center',
  padding: theme.spacing(1.2, 1.8),
  color: theme.palette.text.disabled,
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}));

const DataRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '48px 1.6fr 0.9fr 0.6fr 1fr 1.2fr 0.8fr 52px',
  gap: theme.spacing(1.5),
  alignItems: 'center',
  padding: theme.spacing(2),
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
  boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
  [theme.breakpoints.down('lg')]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1.5),
  },
}));

const SideCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
}));

export default function ModelsView() {
  return {
    main: (
      <MainCard>
        <Stack spacing={2.5}>
          <Banner>
            <Stack direction="row" alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2}>
              <Stack direction="row" spacing={1.2} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ minWidth: 0 }}>
                <AutoAwesomeRoundedIcon sx={{ color: 'primary.main', mt: { xs: 0.2, sm: 0 } }} />
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  Get better results with Neural Architecture Search. NAS automatically explores architectures to find the optimal model for your dataset.
                </Typography>
              </Stack>
              <Button variant="outlined" size="small" sx={{ borderRadius: 2, textTransform: 'none', flexShrink: 0 }}>
                Try NAS
              </Button>
            </Stack>
          </Banner>

          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <ModelTrainingRoundedIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h4" sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 700 }}>
                Models
              </Typography>
            </Stack>
            <Button
              variant="contained"
              endIcon={<ExpandMoreRoundedIcon />}
              sx={{
                borderRadius: 2.5,
                textTransform: 'none',
                px: 2,
                background: (theme) => theme.palette.gradients.primary,
                boxShadow: (theme) => theme.customShadows?.primary || 'none',
                '&:hover': {
                  background: (theme) => theme.palette.gradients.primary,
                  boxShadow: (theme) => theme.customShadows?.primary || 'none',
                },
              }}
            >
              Train Model
            </Button>
          </Stack>

          <Tabs value={0} sx={{ minHeight: 40, '& .MuiTabs-indicator': { height: 2 } }}>
            <Tab
              disableRipple
              label={<Stack direction="row" spacing={1} alignItems="center"><span>Fine-tuned</span><Chip label="1" size="small" sx={{ height: 20 }} /></Stack>}
              sx={{ minHeight: 40, textTransform: 'none', px: 1.5 }}
            />
            <Tab
              disableRipple
              label={<Stack direction="row" spacing={1} alignItems="center"><span>Universe</span><Chip label="0" size="small" sx={{ height: 20 }} /></Stack>}
              sx={{ minHeight: 40, textTransform: 'none', px: 1.5 }}
            />
          </Tabs>

          <Divider />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                size="small"
                placeholder="Search models..."
                sx={{ minWidth: { sm: 220 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Select size="small" defaultValue="all" sx={{ minWidth: 140, borderRadius: 2 }}>
                <MenuItem value="all">Model Type All</MenuItem>
                <MenuItem value="instant">Roboflow Instant</MenuItem>
              </Select>
            </Stack>

            <Select size="small" defaultValue="newest" sx={{ minWidth: 170, borderRadius: 2 }}>
              <MenuItem value="newest">Sort By Newest First</MenuItem>
              <MenuItem value="oldest">Sort By Oldest First</MenuItem>
            </Select>
          </Stack>

          <Box sx={{ borderRadius: 14, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <DataHeader>
              <span />
              <span>Model Name</span>
              <span>Updated</span>
              <span>Metrics</span>
              <span>Type</span>
              <span>Dataset Version</span>
              <span>License</span>
              <span />
            </DataHeader>

            <DataRow>
              <Avatar
                variant="rounded"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 2.5,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                  color: 'primary.main',
                }}
              >
                <BoltRoundedIcon sx={{ fontSize: 18 }} />
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Roboflow Instant <Typography component="span" variant="caption" sx={{ color: 'primary.main' }}>(v1)</Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ID: test-4lyvz/movable-objects-instant-1
                </Typography>
              </Box>

              <Stack spacing={0.4}>
                <Stack direction="row" spacing={0.6} alignItems="center">
                  <CheckBoxRoundedIcon sx={{ color: 'success.main', fontSize: 18 }} />
                  <Typography variant="body2">21/04/2026</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  15:12
                </Typography>
              </Stack>

              <Typography variant="body2" color="text.secondary">
                -
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Roboflow Instant
              </Typography>

              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                Fine-tuned on your dataset.
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Standard
              </Typography>

              <IconButton sx={{ width: 36, height: 36, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <LaunchRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </DataRow>
          </Box>
        </Stack>
      </MainCard>
    ),
    side: (
      <Stack spacing={2}>
        <SideCard>
          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Model Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fine-tuned models trained for this project will appear here with updated timestamps and deployment-ready links.
            </Typography>
          </Stack>
        </SideCard>

        <SideCard>
          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Training Tips
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Keep dataset versions stable before training and compare model types using the same split for useful evaluation.
            </Typography>
          </Stack>
        </SideCard>
      </Stack>
    ),
  };
}
